import { AddNewScript } from '@/components/escript/token/AddNewScript';
import type {
  AddNewScriptFormFields,
  AddNewScriptFormTokenUnion,
  ReviewNewScriptInterface,
} from '@/components/escript/token/types';
import { GoogleAnalyticsEscriptEventName } from '@/hooks/analytics/types';
import useGoogleAnalytics from '@/hooks/analytics/useGoogleAnalytics';
import { hashedItem } from '@/hooks/analytics/utils';
import { useScriptTokenContext } from '@/hooks/escript/useScriptTokenContext';
import type { SubmitTokenSuccessCallbackProps } from '@/hooks/escript/useSubmitToken';
import { useSubmitToken } from '@/hooks/escript/useSubmitToken';
import useCustomer from '@/hooks/user/useCustomer';
import { emitUIEvent } from '@montugroup/data-collection';
import { FormProvider, useForm } from 'react-hook-form';

function AddNewScriptWrapper() {
  const { setAddNewScriptsState, setReviewNewScriptsState, setNewSubmittedScripts } = useScriptTokenContext();
  const { sendGoogleAnalyticsEvent } = useGoogleAnalytics();
  const { data: customer } = useCustomer();
  const { parsedTokenMutate } = useSubmitToken({
    onSuccessCallback: (props: SubmitTokenSuccessCallbackProps) => {
      const { productName, productMatchValidationStatus, productStatus, isProductMatched, token, erxStatus } = props;
      const newScript: ReviewNewScriptInterface = {
        token,
        productName,
        productMatchValidationStatus,
        isProductMatched,
        erxStatus,
        productStockStatus: productStatus,
        isError: false,
      };

      emitUIEvent(
        {
          component_name: 'add_new_script_token',
          event_name: 'success',
          id: 'escript_add_new_script_token_success',
          value: `Submit token success`,
        },
        [],
      );
      setNewSubmittedScripts((newSubmittedScripts) => [...newSubmittedScripts, newScript]);
    },
    onErrorCallback: (token: string) => {
      setNewSubmittedScripts((newSubmittedScripts) => [
        ...newSubmittedScripts,
        {
          token: token,
          isError: true,
          isProductMatched: false,
        },
      ]);

      // GA event to track validation failures
      sendGoogleAnalyticsEvent(GoogleAnalyticsEscriptEventName.PRESCRIPTION_SUBMIT_ERROR, {
        error_code: 'Submit token error - prescription unavailable',
        circuit_identifier: customer?.code ? hashedItem(customer.code) : '',
      });

      emitUIEvent(
        {
          component_name: 'add_new_script_token',
          event_name: 'error',
          id: 'escript_add_new_script_token_error',
          value: `Submit token error - prescription unavailable`,
        },
        [],
      );
    },
  });

  const form = useForm<AddNewScriptFormTokenUnion>({
    defaultValues: {
      tokenInput: '',
    },
  });

  const submitTokenMutation = (tokenString: string) => {
    parsedTokenMutate({ body: { data: { token: tokenString } } });
  };

  const handleAddNewScriptSubmit = async (value: AddNewScriptFormFields) => {
    setNewSubmittedScripts([]);

    for (let i = 0; i < Object.values(value).length; i++) {
      submitTokenMutation(Object.values(value)[i]);
    }
    setAddNewScriptsState(false);
    setReviewNewScriptsState(true);
  };

  return (
    <FormProvider {...form}>
      <AddNewScript handleTokenSubmit={handleAddNewScriptSubmit} />
    </FormProvider>
  );
}

export default AddNewScriptWrapper;
